
import { useState, useEffect } from 'react';
import { useOptimizedLocalStorage } from './useOptimizedLocalStorage';

interface CheckoutState {
  email: string;
  name: string;
  referralId: string | null;
  timestamp: number;
  step: string;
  sessionId?: string;
}

export const useStateRecovery = () => {
  const { getItem, removeItem } = useOptimizedLocalStorage();
  const [recoveredState, setRecoveredState] = useState<CheckoutState | null>(null);

  useEffect(() => {
    console.log("[STATE RECOVERY] Hook iniciado, intentando recuperar estado...");
    
    const recoverState = () => {
      try {
        // Primero intentar recuperar desde múltiples ubicaciones
        let checkoutState = null;
        
        // Intentar desde sessionStorage
        try {
          const sessionData = sessionStorage.getItem('checkout_state');
          if (sessionData) {
            checkoutState = JSON.parse(sessionData);
            console.log("[STATE RECOVERY] Estado encontrado en sessionStorage:", checkoutState);
          }
        } catch (e) {
          console.error("[STATE RECOVERY] Error leyendo sessionStorage:", e);
        }
        
        // Si no hay en sessionStorage, intentar localStorage
        if (!checkoutState) {
          try {
            const localData = localStorage.getItem('checkout_state');
            if (localData) {
              checkoutState = JSON.parse(localData);
              console.log("[STATE RECOVERY] Estado encontrado en localStorage:", checkoutState);
            }
          } catch (e) {
            console.error("[STATE RECOVERY] Error leyendo localStorage:", e);
          }
        }

        if (checkoutState) {
          const now = Date.now();
          const stateAge = now - (checkoutState.timestamp || 0);
          
          console.log("[STATE RECOVERY] Estado encontrado:", checkoutState);
          console.log("[STATE RECOVERY] Edad del estado (minutos):", stateAge / (1000 * 60));
          
          // Usar el estado si es reciente (menos de 2 horas para dar más margen)
          if (stateAge < 2 * 60 * 60 * 1000) {
            console.log("[STATE RECOVERY] Estado válido, aplicando...");
            setRecoveredState(checkoutState);
            return checkoutState;
          } else {
            console.log("[STATE RECOVERY] Estado demasiado viejo, descartando");
            clearSavedState();
          }
        }

        // Intentar recuperar datos individuales como fallback
        const email = sessionStorage.getItem('pending_email') || localStorage.getItem('pending_email');
        const name = sessionStorage.getItem('pending_name') || localStorage.getItem('pending_name');
        const referralId = sessionStorage.getItem('rewardful_referral_id') || localStorage.getItem('rewardful_referral_id');

        console.log("[STATE RECOVERY] Datos individuales:", { email, name, referralId });

        if (email || name || referralId) {
          const fallbackState: CheckoutState = {
            email: email || '',
            name: name || '',
            referralId: referralId,
            timestamp: Date.now(),
            step: 'fallback_recovery'
          };
          console.log("[STATE RECOVERY] Creando estado de fallback:", fallbackState);
          setRecoveredState(fallbackState);
          return fallbackState;
        }

        console.log("[STATE RECOVERY] No se encontró ningún estado para recuperar");
      } catch (error) {
        console.error("[STATE RECOVERY] Error recuperando estado:", error);
        clearSavedState();
      }
      
      return null;
    };

    // Ejecutar inmediatamente
    recoverState();
    
    // También ejecutar cuando la ventana recibe el foco (útil para móviles)
    const handleFocus = () => {
      console.log("[STATE RECOVERY] Ventana recibió foco, verificando estado...");
      recoverState();
    };
    
    window.addEventListener('focus', handleFocus);
    
    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, [getItem]);

  const clearSavedState = () => {
    try {
      console.log("[STATE RECOVERY] Limpiando estado guardado...");
      
      // Limpiar todo el estado relacionado
      const keysToRemove = [
        'checkout_state',
        'pending_email',
        'pending_name',
        'rewardful_referral_id'
      ];
      
      keysToRemove.forEach(key => {
        try {
          localStorage.removeItem(key);
          sessionStorage.removeItem(key);
        } catch (e) {
          console.error(`[STATE RECOVERY] Error eliminando ${key}:`, e);
        }
      });
      
      setRecoveredState(null);
      console.log("[STATE RECOVERY] Estado limpiado completamente");
    } catch (error) {
      console.error("[STATE RECOVERY] Error limpiando estado:", error);
    }
  };

  return {
    recoveredState,
    clearSavedState,
    hasRecoveredState: !!recoveredState
  };
};
